// Centralized API fetch wrapper for ShahoSuite
// Handles auth, session expiration, and redirects

const redirectAndThrow = (path: string, reason: string): never => {
  window.location.href = path;
  throw new Error(reason);
};

export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Clone options to avoid mutating caller's object
  const opts: RequestInit = { ...options };

  // Add Authorization header if token exists
  const token = localStorage.getItem('token');
  opts.headers = {
    ...(opts.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  // Always include credentials for cross-origin
  opts.credentials = opts.credentials || 'include';

  const res = await fetch(url, opts);

  // Handle special status codes
  if (res.status === 401) {
    let body: any = {};
    try {
      body = await res.json();
    } catch {}
    if (body.error === 'session_expired') {
      localStorage.removeItem('token');
      sessionStorage.clear();
      alert('You have been logged in on another device. Please log in again.');
      return redirectAndThrow('/session-expired', 'Session expired');
    }
    if (body.error === 'session_expired_inactivity') {
      localStorage.removeItem('token');
      sessionStorage.clear();
      alert(body.message || 'Platform session expired due to inactivity. Please sign in again.');
      return redirectAndThrow('/login', 'Session expired due to inactivity');
    }
    // Any other 401
    localStorage.removeItem('token');
    return redirectAndThrow('/login', 'Unauthorized');
  }
  if (res.status === 402) {
    return redirectAndThrow('/subscription-inactive', 'Subscription inactive');
  }
  if (res.status === 403) {
    let body: any = {};
    try {
      body = await res.json();
    } catch {}
    if (body.error === 'admin_2fa_required') {
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/system?tab=security';
      }
      throw new Error(body.message || 'Two-factor authentication required');
    }
    if (body.error === 'ip_not_allowed') {
      alert(body.message || 'Your IP is not authorized for platform admin access.');
      throw new Error('IP not allowed');
    }
    if (body.error === 'password_change_required') {
      return redirectAndThrow('/change-password', 'Password change required');
    }
    return redirectAndThrow('/change-password', 'Forbidden');
  }

  return res;
}
